import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom'
import Seo from '../../components/Seo'
import { api } from '../../api/client'
import { useSession } from '../../api/session'
import { brand } from '../../brand'
import type { Switches } from '../../api/types'

export default function AppLayout() {
  const { user, logout } = useSession()
  const navigate = useNavigate()
  const [budgetWarning, setBudgetWarning] = useState<number | null>(null)
  const [switches, setSwitches] = useState<Switches | null>(null)

  useEffect(() => {
    let alive = true
    const tick = () => api.switches().then((s) => alive && setSwitches(s)).catch(() => {})
    const checkBudget = () => api.budgetAlerts().then(result => {
      if (alive) setBudgetWarning(result.alerts.some(a => a.active) ? result.percent_used : null)
    }).catch(() => {})
    tick(); void checkBudget()
    const budgetTimer = window.setInterval(checkBudget, 10_000)
    const id = window.setInterval(tick, 10_000)
    return () => {
      alive = false
      window.clearInterval(id)
      window.clearInterval(budgetTimer)
    }
  }, [])

  const off = switches ? !switches.global.enabled : false
  const engagementsOff = switches?.engagements.filter((e) => !e.enabled).length ?? 0

  return (
    <div className="app">
      <Seo title="Dashboard" noindex />
      <aside className="app-side">
        <Link to="/app" className="wordmark">
          {brand.shortName} <small>Analyst</small>
        </Link>
        <nav className="app-nav" aria-label="Dashboard">
          <NavLink to="/app" end>
            Overview
          </NavLink>
          <NavLink to="/app/companies">Companies</NavLink>
          <NavLink to="/app/spend">Spend</NavLink>
          <NavLink to="/app/prompts">Prompts</NavLink>
          <NavLink to="/app/inbox">Inbox</NavLink>
          <NavLink to="/app/demos">Demos</NavLink>
          <NavLink to="/app/activity">Activity</NavLink>
          <NavLink to="/app/controls">
            Controls
            <span className={`dot${off ? ' off' : engagementsOff ? ' warn' : ''}`} aria-hidden="true" />
          </NavLink>
        </nav>
        <div className="spacer" />
        <div className="app-user">
          <span>
            {user?.name}
            <br />
            <span className="muted">{user?.email}</span>
          </span>
          <button
            className="btn secondary small"
            onClick={async () => {
              await logout()
              navigate('/login')
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <div className="app-main">
        {switches && (
          <div className={`switch-banner${off ? ' off' : ''}`} role="status">
            <span className={`dot${off ? ' off' : engagementsOff ? ' warn' : ''}`} aria-hidden="true" />
            {off ? (
              <>
                <strong>Outbound is switched off.</strong> {switches.paused_jobs} workflows are waiting; {switches.held_jobs} blocked workflows need review.
              </>
            ) : engagementsOff ? (
              <>
                Outbound on. {engagementsOff} engagement{engagementsOff === 1 ? '' : 's'} paused.
              </>
            ) : (
              <>Outbound enabled; each company must pass delivery checks.</>
            )}
            <Link to="/app/controls" style={{ marginLeft: 'auto' }}>
              Controls
            </Link>
          </div>
        )}
        {budgetWarning !== null && <div className={`notice ${budgetWarning >= 100 ? 'crit' : ''}`} role="status">Lifetime budget: {budgetWarning.toFixed(1)}% committed, including reservations. <Link to="/app/spend">Review spending</Link></div>}
      <Outlet />
      </div>
    </div>
  )
}
